// stpUtils.ts — STP Phase 2 Pay Event payload builder
// Generates ATO-compliant JSON ready for lodgement via:
//   - Employment Hero API  (connect: VITE_EMPLOYMENT_HERO_API_KEY)
//   - KeyPay API           (connect: VITE_KEYPAY_API_KEY)
//   - Xero Payroll         (auto via Xero OAuth — no extra credentials once Xero is connected)
//   - Manual lodgement     (download JSON, upload to ATO business portal via your intermediary)
//
// STP Phase 2 mandatory from 1 Jan 2022 (ATO)
// Reference: ATO STP Phase 2 Employer Reporting Guidelines v1.2

import { getEffectiveRate } from '../pages/Settings'
import { calcHours, getNightShiftLoading } from './mockDataExtended'
import { getPayRates } from '../pages/Settings'

// ── ATO employment basis codes ─────────────────────────────────────
// F = Full time  P = Part time  C = Casual  L = Labour hire  N = Non-employee
export type EmploymentBasis = 'F' | 'P' | 'C' | 'L' | 'N'

// ── ATO tax scale types ────────────────────────────────────────────
// R = Regular   S = Seniors   H = Working holiday maker
export type TaxScaleType = 'R' | 'S' | 'H'

// ── Allowance type codes (ATO STP Phase 2 Table 3) ────────────────
// TD = Tool/equipment  CD = Car  RD = Award transport  OD = Other
export type AllowanceTypeCode = 'TD' | 'CD' | 'RD' | 'AD' | 'LD' | 'OD'

export interface STPAllowance {
  typeCode:     AllowanceTypeCode
  description:  string
  amount:       number
  ytdAmount:    number
}

export interface STPPayeeRecord {
  // Identification
  payeeId:            string
  employeeName:       string
  tfn:                string      // Tax File Number — '000000000' if declaration pending
  tfnPending:         boolean     // true if TFN not yet provided
  familyName:         string
  givenName:          string
  otherGivenNames?:   string
  dateOfBirth?:       string      // YYYY-MM-DD
  employmentBasis:    EmploymentBasis
  taxScaleType:       TaxScaleType
  incomeType:         'SAW'       // Salary and wages (standard for all MCB Civil employees)
  classification:     string      // for display only
  // Earnings — this pay period
  grossEarnings:           number  // total gross inc allowances
  ordinaryTimeEarnings:    number  // for super calculation base (excl OT)
  overtimeEarnings:        number
  allowances:              STPAllowance[]
  // Statutory — this pay period
  paygWithholding:         number  // PAYG tax withheld
  superGuarantee:          number  // 11.5% employer SG
  // Year-to-date (FY1 July – this pay event)
  // In production these come from the payroll ledger / intermediary
  // For prototype: estimated from pay period × weeks elapsed in FY
  ytdGrossEarnings:        number
  ytdPaygWithholding:      number
  ytdSuperGuarantee:       number
  ytdOrdinaryTimeEarnings: number
  ytdOvertimeEarnings:     number
  // Hours breakdown (for reconciliation — not ATO mandatory but useful for audit)
  hoursOrdinary:   number
  hoursOvertime:   number
  hoursNightLoad:  number
  payRate:         number
}

export interface STPPayEvent {
  // Header
  messageId:        string      // unique per submission (UUID or timestamp)
  declarationDate:  string      // date PM signs the declaration
  paymentDate:      string      // date employees are paid (bank credit date)
  payPeriodFrom:    string      // first day of pay period
  payPeriodTo:      string      // last day of pay period
  // Payer
  payerABN:         string
  payerBranchNumber: number
  payerName:        string
  // Software
  softwareId:       string      // ATO-registered Software Product ID (SPID)
  softwareVersion:  string
  generatedAt:      string      // ISO timestamp
  generatedByName:  string
  // Payees
  payeeCount:       number
  payees:           STPPayeeRecord[]
  // Totals
  totalGross:       number
  totalPAYG:        number
  totalSuper:       number
  totalOTE:         number      // ordinary time earnings (super base)
  // Lodgement
  status:           'PENDING' | 'VALIDATED' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED'
  intermediary?:    'EMPLOYMENT_HERO' | 'KEYPAY' | 'XERO' | 'MANUAL'
  submissionRef?:   string      // returned by intermediary after lodgement
  lodgedAt?:        string
  atoReceiptId?:    string      // returned by ATO after acceptance
}

// ── Helpers ────────────────────────────────────────────────────────
function mapEmploymentBasis(type: string): EmploymentBasis {
  const map: Record<string, EmploymentBasis> = {
    FULL_TIME:   'F',
    PART_TIME:   'P',
    CASUAL:      'C',
    LABOUR_HIRE: 'L',
    CONTRACTOR:  'N',
  }
  return map[type?.toUpperCase()] ?? 'F'
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

// Estimate weeks elapsed in current FY (1 Jul → 30 Jun)
// Used to scale YTD figures from current-period amounts
function weeksElapsedInFY(): number {
  const today  = new Date()
  const fyStart = new Date(
    today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1,
    6, 1  // 1 July
  )
  const msPerWeek = 7 * 24 * 60 * 60 * 1000
  return Math.max(1, Math.round((today.getTime() - fyStart.getTime()) / msPerWeek))
}

// ── Main builder ───────────────────────────────────────────────────
export function buildSTPPayEvent(options: {
  timesheets:    any[]
  employees:     any[]
  payerABN:      string
  payerName:     string
  generatedByName: string
}): STPPayEvent {
  const { timesheets, employees, payerABN, payerName, generatedByName } = options
  const rates       = getPayRates()
  const approved    = timesheets.filter(ts => ts.status === 'APPROVED')
  const weeksInFY   = weeksElapsedInFY()
  const now         = new Date()

  const payees: STPPayeeRecord[] = approved.map(ts => {
    const emp     = employees.find((e: any) => e.id === ts.employeeId) as any
    const rate    = getEffectiveRate(emp?.payClassification ?? '', (emp as any)?.isAdultApprentice ?? false)
    const isTrade = ['ELECTRICAL', 'CIVIL'].includes(emp?.trade ?? '')

    // Hours
    let ordHrs = 0, otHrs = 0, nightLoadHrs = 0
    for (const entry of ts.entries) {
      if (!entry.start || !entry.end) continue
      const h = calcHours(entry.start, entry.end, entry.break ?? 0, entry.dayIndex, entry.shiftType ?? 'DAY')
      ordHrs       += h.ordinary
      otHrs        += h.overtime + h.doubleTime
      nightLoadHrs += (h.nightLoading ?? 0)
    }

    // Earnings
    const ordPay       = round2(ordHrs * rate)
    const nightLoadPay = round2(nightLoadHrs * rate)
    const otPay        = round2(otHrs * rate * (rates.otMultiplier ?? 1.5))
    const toolAllow    = isTrade ? round2(rates.allowances?.toolAllowance ?? 0) : 0
    const grossEarnings        = round2(ordPay + nightLoadPay + otPay + toolAllow)
    const ordinaryTimeEarnings = round2(ordPay + nightLoadPay)  // OTE = ordinary + night loading

    // Statutory
    const paygWithholding = round2(grossEarnings * 0.185)
    const superGuarantee  = round2(grossEarnings * 0.115)

    // YTD (scaled estimate)
    const ytdGross = round2(grossEarnings * weeksInFY)
    const ytdPAYG  = round2(paygWithholding * weeksInFY)
    const ytdSuper = round2(superGuarantee * weeksInFY)
    const ytdOTE   = round2(ordinaryTimeEarnings * weeksInFY)
    const ytdOT    = round2(otPay * weeksInFY)

    // Allowances (ATO Phase 2 — each type reported separately)
    const allowances: STPAllowance[] = []
    if (toolAllow > 0) {
      allowances.push({
        typeCode:    'TD',
        description: 'Tool and equipment allowance',
        amount:      toolAllow,
        ytdAmount:   round2(toolAllow * weeksInFY),
      })
    }

    // Name split
    const nameParts  = (ts.employeeName ?? '').trim().split(/\s+/)
    const givenName  = nameParts[0] ?? ''
    const familyName = nameParts.slice(1).join(' ') || givenName
    const tfn        = (emp as any)?.taxFileNumber ?? '000000000'

    return {
      payeeId:              ts.employeeId,
      employeeName:         ts.employeeName,
      tfn,
      tfnPending:           tfn === '000000000' || !tfn,
      familyName,
      givenName,
      employmentBasis:      mapEmploymentBasis(emp?.employmentType ?? 'FULL_TIME'),
      taxScaleType:         'R',
      incomeType:           'SAW',
      classification:       (emp?.payClassification ?? '').replace(/_/g, ' '),
      grossEarnings,
      ordinaryTimeEarnings,
      overtimeEarnings:     otPay,
      allowances,
      paygWithholding,
      superGuarantee,
      ytdGrossEarnings:     ytdGross,
      ytdPaygWithholding:   ytdPAYG,
      ytdSuperGuarantee:    ytdSuper,
      ytdOrdinaryTimeEarnings: ytdOTE,
      ytdOvertimeEarnings:  ytdOT,
      hoursOrdinary:        round2(ordHrs),
      hoursOvertime:        round2(otHrs),
      hoursNightLoad:       round2(nightLoadHrs),
      payRate:              rate,
    }
  })

  // Pay period dates from first approved timesheet
  const weekStart = approved[0]?.weekStarting ?? now.toISOString().slice(0, 10)
  const weekEnd   = new Date(new Date(weekStart).getTime() + 6 * 86400000).toISOString().slice(0, 10)

  return {
    messageId:         `STP2-MCB-${now.getTime()}`,
    declarationDate:   now.toISOString().slice(0, 10),
    paymentDate:       now.toISOString().slice(0, 10),
    payPeriodFrom:     weekStart,
    payPeriodTo:       weekEnd,
    payerABN:          payerABN.replace(/\s/g, ''),
    payerBranchNumber: 1,
    payerName,
    softwareId:        'TRAFFICPROCURE-STP2-001',   // Register at ato.gov.au/stp when going live
    softwareVersion:   '1.0.0',
    generatedAt:       now.toISOString(),
    generatedByName,
    payeeCount:        payees.length,
    payees,
    totalGross:        round2(payees.reduce((s, p) => s + p.grossEarnings, 0)),
    totalPAYG:         round2(payees.reduce((s, p) => s + p.paygWithholding, 0)),
    totalSuper:        round2(payees.reduce((s, p) => s + p.superGuarantee, 0)),
    totalOTE:          round2(payees.reduce((s, p) => s + p.ordinaryTimeEarnings, 0)),
    status:            'PENDING',
  }
}
